import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { env } from "@/app/_utils/env";
import {
  fetchAuthorizationUrl,
  fetchMe,
  postPasswordLogin,
} from "@/app/_services/authApi";
import { clearCsrfToken, setCsrfToken } from "@/app/_utils/csrf";
import { SUPER_ADMIN_ROLE_NAME } from "@/app/_utils/permissions";
import type { ClientSession, MePayload } from "@/app/_utils/authTypes";

export type { ClientSession, MePayload } from "@/app/_utils/authTypes";

// Kept only so a full page reload can render the shell before auth/me/ resolves. It is
// a UI convenience, never an authorization source -- the server decides on every call.
const UI_USER_KEY = "cuo_user_profile";

type SessionAuthContextValue = {
  session: ClientSession | null;
  status: "loading" | "authenticated" | "unauthenticated";
  can: (permission: string) => boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (
    email: string,
    password: string
  ) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  getSession: () => Promise<ClientSession | null>;
};

const SessionAuthContext = createContext<SessionAuthContextValue | null>(null);

function toSession(payload: MePayload): ClientSession {
  return {
    user: payload.user,
    roles: payload.roles ?? [],
    permissions: new Set(payload.permissions ?? []),
  };
}

function readCachedSession(): ClientSession | null {
  try {
    const raw = localStorage.getItem(UI_USER_KEY);
    if (!raw) return null;
    return toSession(JSON.parse(raw) as MePayload);
  } catch {
    return null;
  }
}

export function SessionAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [session, setSession] = useState<ClientSession | null>(readCachedSession);
  const [status, setStatus] = useState<
    "loading" | "authenticated" | "unauthenticated"
  >("loading");

  const refresh = useCallback(async () => {
    const payload = await fetchMe();
    if (!payload) {
      localStorage.removeItem(UI_USER_KEY);
      clearCsrfToken();
      setSession(null);
      setStatus("unauthenticated");
      return;
    }
    setCsrfToken(payload.csrf_token);
    localStorage.setItem(UI_USER_KEY, JSON.stringify(payload));
    setSession(toSession(payload));
    setStatus("authenticated");
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const applyPayload = useCallback((payload: MePayload) => {
    setCsrfToken(payload.csrf_token);
    localStorage.setItem(UI_USER_KEY, JSON.stringify(payload));
    setSession(toSession(payload));
    setStatus("authenticated");
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const url = await fetchAuthorizationUrl();
    if (url) window.location.href = url;
  }, []);

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      const result = await postPasswordLogin(email, password);
      if (!result.ok || !result.payload) {
        return { ok: false, error: result.error };
      }
      applyPayload(result.payload);
      return { ok: true };
    },
    [applyPayload]
  );

  const logout = useCallback(async () => {
    try {
      await fetch(`${env.apiBaseUrl}auth/logout/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
    } finally {
      localStorage.removeItem(UI_USER_KEY);
      clearCsrfToken();
      setSession(null);
      setStatus("unauthenticated");
    }
  }, []);

  const can = useCallback(
    (permission: string) => {
      if (!session) return false;
      if (session.roles.includes(SUPER_ADMIN_ROLE_NAME)) return true;
      return session.permissions.has(permission);
    },
    [session]
  );

  const getSession = useCallback(async () => session, [session]);

  const value = useMemo(
    () => ({
      session,
      status,
      can,
      signInWithGoogle,
      signInWithPassword,
      logout,
      refresh,
      getSession,
    }),
    [
      session,
      status,
      can,
      signInWithGoogle,
      signInWithPassword,
      logout,
      refresh,
      getSession,
    ]
  );

  return (
    <SessionAuthContext.Provider value={value}>
      {children}
    </SessionAuthContext.Provider>
  );
}

export function useSessionAuth() {
  const ctx = useContext(SessionAuthContext);
  if (!ctx) {
    throw new Error("useSessionAuth must be used within SessionAuthProvider");
  }
  return ctx;
}

export function useCan(permission: string): boolean {
  return useSessionAuth().can(permission);
}

/** Synchronous read of the cached profile, for the few non-React callers. */
export async function getSession(): Promise<ClientSession | null> {
  return readCachedSession();
}

export function signOut() {
  localStorage.removeItem(UI_USER_KEY);
  clearCsrfToken();
  void fetch(`${env.apiBaseUrl}auth/logout/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  }).finally(() => {
    window.location.href = "/login";
  });
}
